import React, { useState, useEffect, useRef } from 'react';
import { websocketService } from '../services/websocket';
import { getApiUrl } from '../lib/config';

interface ChatMessage {
  id: number;
  username: string;
  message: string;
  timestamp: string;
  avatar?: string;
}

interface ChatProps {
  streamId: number;
}

const Chat: React.FC<ChatProps> = ({ streamId }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [username, setUsername] = useState('');
  const [userAvatar, setUserAvatar] = useState('');
  const [hasSetUsername, setHasSetUsername] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Random avatars pool
  const avatars = [
    '👤', '👥', '🧑', '👨', '👩', '🧒', '👦', '👧', '🧓', '👴', '👵',
    '😀', '😃', '😄', '🙂', '😊', '🤗', '🤠', '🥳', '😎', '🤓',
    '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯',
    '🚀', '⭐', '🌟', '✨', '🎯', '🎪', '🎨', '🎭', '🎪', '🎸'
  ];

  // Common emojis for the picker
  const emojis = [
    '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇',
    '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
    '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩',
    '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
    '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
    '✨', '⭐', '🌟', '💫', '⚡', '💥', '💢', '💨', '💦', '💤'
  ];

  // Get random avatar for user
  const getRandomAvatar = () => {
    return avatars[Math.floor(Math.random() * avatars.length)];
  };

  // Load saved username and avatar
  useEffect(() => {
    const savedUsername = localStorage.getItem('chat_username');
    const savedAvatar = localStorage.getItem('chat_avatar');
    if (savedUsername) {
      setUsername(savedUsername);
      setHasSetUsername(true);
    }
    if (savedAvatar) {
      setUserAvatar(savedAvatar);
    }
  }, []);

  useEffect(() => {
    loadMessages();
    
    if (hasSetUsername) {
      connectWebSocket();
    }

    return () => {
      websocketService.disconnect();
    };
  }, [streamId, hasSetUsername]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const connectWebSocket = () => {
    try {
      websocketService.connect();
      
      websocketService.on('connected', (connected: boolean) => {
        console.log('WebSocket connected:', connected);
        setIsConnected(connected);
        if (connected) {
          setConnectionError(null);
          // Join stream after connection is established
          console.log('Joining stream with ID:', streamId);
          websocketService.joinStream(streamId);
        }
      });

      websocketService.on('connection-failed', (error: string) => {
        setConnectionError(error);
        setIsConnected(false);
      });

      websocketService.on('chat-message', (message: ChatMessage) => {
        console.log('Received WebSocket message:', message);
        setMessages(prev => [...prev, message]);
      });

      websocketService.on('chat-error', (error: string) => {
        setConnectionError(error);
      });
    } catch (error) {
      console.error('Failed to connect to chat:', error);
      setConnectionError('Failed to connect to real-time chat');
    }
  };

  const loadMessages = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(getApiUrl(`api/chat/messages?streamId=${streamId}&limit=50`));
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetUsername = () => {
    if (!username.trim()) {
      alert('Please enter a username');
      return;
    }
    
    const trimmedUsername = username.trim();
    const assignedAvatar = getRandomAvatar();
    setUsername(trimmedUsername);
    setUserAvatar(assignedAvatar);
    setHasSetUsername(true);
    localStorage.setItem('chat_username', trimmedUsername);
    localStorage.setItem('chat_avatar', assignedAvatar);
    connectWebSocket();
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    const message = newMessage.trim();
    setNewMessage('');

    console.log('Sending message:', { streamId, message, username });

    // Try WebSocket first
    if (isConnected && websocketService.isConnected()) {
      console.log('Sending via WebSocket');
      websocketService.sendChatMessage({
        streamId,
        message,
        username,
        avatar: userAvatar
      });
    } else {
      // Fallback to HTTP API
      console.log('Sending via HTTP API');
      try {
        const payload = {
          streamId,
          message,
          username,
          avatar: userAvatar,
          userId: null // Guest user
        };
        
        console.log('HTTP payload:', payload);
        
        const response = await fetch(getApiUrl('api/chat/messages'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        console.log('HTTP response status:', response.status);
        
        if (response.ok) {
          const result = await response.json();
          console.log('Message sent successfully:', result);
          // Add the message directly to the UI instead of refreshing all messages
          const newChatMessage: ChatMessage = {
            id: result.data.id,
            username,
            message,
            avatar: userAvatar,
            timestamp: result.data.timestamp || new Date().toISOString()
          };
          setMessages(prev => [...prev, newChatMessage]);
        } else {
          const errorData = await response.text();
          console.error('HTTP error response:', errorData);
          throw new Error(`Failed to send message: ${response.status}`);
        }
      } catch (error) {
        console.error('Error sending message:', error);
        setNewMessage(message); // Restore message on error
        alert(`Failed to send message: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addEmoji = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const getConnectionStatusIcon = () => {
    if (isConnected) {
      return "🟢";
    } else if (connectionError?.includes('sleeping')) {
      return "🟡";
    } else if (connectionError) {
      return "🔴";
    } else {
      return "🔄";
    }
  };

  const changeUsername = () => {
    setHasSetUsername(false);
    localStorage.removeItem('chat_username');
    localStorage.removeItem('chat_avatar');
    websocketService.disconnect();
  };

  if (!hasSetUsername) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Tahoma, sans-serif',
        backgroundColor: '#f0f0f0'
      }}>
        {/* Title Bar */}
        <div style={{
          background: 'linear-gradient(180deg, #0078d4 0%, #106ebe 100%)',
          color: 'white',
          padding: '8px 12px',
          borderBottom: '2px solid #003c71',
          fontSize: '11px',
          fontWeight: 'bold'
        }}>
          💬 Chats
        </div>
        
        {/* Username Setup */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          backgroundColor: 'white',
          border: '2px inset #c0c0c0',
          margin: '8px',
          padding: '20px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>💬</div>
          <div style={{ fontSize: '12px', marginBottom: '20px', textAlign: 'center', color: '#333' }}>
            Welcome to the live chat!<br />
            Choose a username to start chatting
          </div>
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSetUsername();
                }
              }}
              placeholder="Enter your username..."
              maxLength={20}
              style={{
                padding: '6px',
                border: '2px inset #c0c0c0',
                borderRadius: '2px',
                fontSize: '11px',
                fontFamily: 'Tahoma, sans-serif',
                backgroundColor: 'white',
                width: '150px'
              }}
            />
            <button
              onClick={handleSetUsername}
              disabled={!username.trim()}
              style={{
                padding: '6px 12px',
                backgroundColor: username.trim() ? '#0078d4' : '#ccc',
                color: 'white',
                border: '2px outset #c0c0c0',
                borderRadius: '3px',
                cursor: username.trim() ? 'pointer' : 'not-allowed',
                fontSize: '11px',
                fontFamily: 'Tahoma, sans-serif',
                fontWeight: 'bold'
              }}
            >
              Join Chat
            </button>
          </div>
          
          <div style={{
            fontSize: '9px',
            color: '#666',
            marginTop: '10px',
            textAlign: 'center'
          }}>
            Your avatar will be assigned randomly! 🎲
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'Tahoma, sans-serif',
      backgroundColor: '#f0f0f0'
    }}>
      {/* Title Bar */}
      <div style={{
        background: 'linear-gradient(180deg, #0078d4 0%, #106ebe 100%)',
        color: 'white',
        padding: '8px 12px',
        borderBottom: '2px solid #003c71',
        fontSize: '11px',
        fontWeight: 'bold',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>💬 Chats - {username}</span>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={changeUsername}
            style={{
              fontSize: '8px',
              padding: '2px 6px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '2px',
              color: 'white',
              cursor: 'pointer'
            }}
            title="Change Username"
          >
            Change
          </button>
          <span style={{ fontSize: '9px', opacity: 0.9 }}>
            {getConnectionStatusIcon()} {messages.length} messages
          </span>
        </div>
      </div>

      {/* Messages Area */}
      <div style={{
        flex: 1,
        backgroundColor: 'white',
        border: '2px inset #c0c0c0',
        margin: '8px',
        padding: '8px',
        overflow: 'auto',
        minHeight: '200px'
      }}>
        {isLoading ? (
          <div style={{ 
            textAlign: 'center', 
            color: '#666', 
            fontSize: '11px',
            padding: '20px'
          }}>
            Loading messages...
          </div>
        ) : messages.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            color: '#666', 
            fontSize: '11px',
            padding: '20px'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>💬</div>
            No messages yet. Be the first to say hello!
          </div>
        ) : (
          <div>
            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  marginBottom: '8px',
                  padding: '6px',
                  backgroundColor: msg.username === username ? '#e6f3ff' : '#f8f8f8',
                  border: msg.username === username ? '1px solid #0078d4' : '1px solid #e0e0e0',
                  borderRadius: '3px'
                }}
              >
                <div style={{
                  fontSize: '16px',
                  marginRight: '8px',
                  flexShrink: 0
                }}>
                  {msg.avatar || '👤'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '2px'
                  }}>
                    <span style={{
                      fontWeight: 'bold',
                      fontSize: '10px',
                      color: msg.username === username ? '#0078d4' : '#333'
                    }}>
                      {msg.username}
                    </span>
                    <span style={{
                      fontSize: '8px',
                      color: '#888'
                    }}>
                      {new Date(msg.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#333',
                    lineHeight: '1.3',
                    wordBreak: 'break-word'
                  }}>
                    {msg.message}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Connection Status */}
      {connectionError && (
        <div style={{
          fontSize: '9px',
          color: '#cc6600',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          padding: '4px 8px',
          margin: '0 8px'
        }}>
          ⚠️ {connectionError}. Messages will sync when you send them.
        </div>
      )}

      {/* Emoji Picker */}
      <div style={{
        backgroundColor: '#f8f8f8',
        margin: '8px 8px 4px 8px',
        position: 'relative'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginBottom: '6px'
        }}>
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              backgroundColor: showEmojiPicker ? '#e6f3ff' : 'white',
              border: '2px outset #c0c0c0',
              borderRadius: '3px',
              cursor: 'pointer',
              fontFamily: 'Tahoma, sans-serif'
            }}
            title="Emoji Picker"
          >
            😀
          </button>
        </div>

        {showEmojiPicker && (
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: '6px',
            width: '200px', // Made narrower
            backgroundColor: 'white',
            border: '2px outset #c0c0c0',
            borderRadius: '3px',
            padding: '8px',
            zIndex: 1000,
            boxShadow: '2px 2px 5px rgba(0,0,0,0.3)',
            maxHeight: '120px',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)', // Reduced from 8 to 6 columns
              gap: '2px'
            }}>
              {emojis.map((emoji, index) => (
                <button
                  key={index}
                  onClick={() => addEmoji(emoji)}
                  style={{
                    border: '1px solid #e0e0e0',
                    backgroundColor: 'white',
                    padding: '4px',
                    cursor: 'pointer',
                    borderRadius: '2px',
                    fontSize: '14px',
                    transition: 'background-color 0.1s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0f8ff';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div style={{
        backgroundColor: '#f8f8f8',
        border: '2px inset #c0c0c0',
        margin: '4px 8px 8px 8px',
        padding: '8px',
        display: 'flex',
        gap: '6px',
        alignItems: 'stretch'
      }}>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              sendMessage();
            }
          }}
          placeholder="Type your message..."
          maxLength={200}
          style={{
            flex: 1,
            padding: '6px',
            border: '2px inset #c0c0c0',
            borderRadius: '2px',
            fontSize: '11px',
            fontFamily: 'Tahoma, sans-serif',
            backgroundColor: 'white'
          }}
        />
        <button
          onClick={sendMessage}
          disabled={!newMessage.trim()}
          style={{
            padding: '6px 12px',
            backgroundColor: newMessage.trim() ? '#0078d4' : '#ccc',
            color: 'white',
            border: '2px outset #c0c0c0',
            borderRadius: '3px',
            cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
            fontSize: '11px',
            fontFamily: 'Tahoma, sans-serif',
            fontWeight: 'bold'
          }}
        >
          Send
        </button>
      </div>

      {/* Character Counter */}
      <div style={{
        fontSize: '8px',
        color: '#888',
        textAlign: 'right',
        margin: '0 12px 8px 0'
      }}>
        {newMessage.length}/200
      </div>
    </div>
  );
};

export default Chat; 